import { useState, useRef } from "react";
import { CATEGORIES } from "../utils/constants";
import { api } from "../utils/api";
import styles from "../styles/theme";

export default function BackupView({ people, user, onRefresh }) {
  const fileInputRef = useRef(null);
  const [importState, setImportState] = useState(null); // null | { status: 'preview' | 'success' | 'error', data?, message? }
  const [restoreMode, setRestoreMode] = useState("replace"); // 'replace' | 'merge'
  const [exporting, setExporting] = useState(false);

  const buildExport = () => {
    return {
      _meta: {
        app: "PeopleGraph",
        version: "1.0",
        exportedAt: new Date().toISOString(),
        exportedBy: user.email,
        totalPeople: people.length,
      },
      people: people,
    };
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await api.exportBackup();
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const dateStr = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `peoplegraph-backup-${dateStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    }
    setExporting(false);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        // Validate structure
        if (!parsed._meta || !parsed.people || !Array.isArray(parsed.people)) {
          setImportState({
            status: "error",
            message:
              "Invalid backup file. Missing required structure (_meta and people array).",
          });
          return;
        }
        if (parsed._meta.app !== "PeopleGraph") {
          setImportState({
            status: "error",
            message: "This file was not exported from PeopleGraph.",
          });
          return;
        }
        // Validate each person has at minimum an id and name
        const invalid = parsed.people.filter((p) => !p.id || !p.name);
        if (invalid.length > 0) {
          setImportState({
            status: "error",
            message: `${invalid.length} entries are missing required fields (id or name).`,
          });
          return;
        }
        // Categorize
        const catCounts = {};
        CATEGORIES.forEach((c) => (catCounts[c.id] = 0));
        parsed.people.forEach((p) => {
          if (catCounts[p.category] !== undefined) catCounts[p.category]++;
        });
        setImportState({ status: "preview", data: parsed, catCounts });
      } catch {
        setImportState({
          status: "error",
          message:
            "Could not parse file. Please ensure it is a valid JSON backup file.",
        });
      }
    };
    reader.readAsText(file);
    // Reset so same file can be re-selected
    e.target.value = "";
  };

  const handleRestore = async () => {
    if (!importState?.data) return;
    try {
      const result = await api.importBackup(importState.data, restoreMode);
      await onRefresh();
      if (restoreMode === "merge") {
        setImportState({
          status: "success",
          message: `Merged ${result.imported} new ${result.imported === 1 ? "person" : "people"}.${result.skipped > 0 ? ` ${result.skipped} duplicate${result.skipped !== 1 ? "s" : ""} skipped.` : ""}`,
        });
      } else {
        setImportState({
          status: "success",
          message: `Restored ${result.imported} ${result.imported === 1 ? "person" : "people"} from backup.`,
        });
      }
    } catch (err) {
      setImportState({
        status: "error",
        message: err.message || "Import failed.",
      });
    }
  };

  return (
    <div style={styles.backupContainer}>
      {/* Export Section */}
      <div style={styles.backupCard}>
        <div style={styles.backupCardIcon}>📤</div>
        <div style={styles.backupCardContent}>
          <h3 style={styles.backupCardTitle}>Export Backup</h3>
          <p style={styles.backupCardDesc}>
            Download a complete copy of your data as a JSON file. This includes
            all {people.length} {people.length === 1 ? "person" : "people"},
            their details, profile pictures, and relationship links.
          </p>
          <div style={styles.backupInfoGrid}>
            <div style={styles.backupInfoItem}>
              <span style={styles.backupInfoLabel}>Total People</span>
              <span style={styles.backupInfoValue}>{people.length}</span>
            </div>
            <div style={styles.backupInfoItem}>
              <span style={styles.backupInfoLabel}>Account</span>
              <span style={styles.backupInfoValue}>{user.email}</span>
            </div>
            <div style={styles.backupInfoItem}>
              <span style={styles.backupInfoLabel}>Format</span>
              <span style={styles.backupInfoValue}>JSON</span>
            </div>
            <div style={styles.backupInfoItem}>
              <span style={styles.backupInfoLabel}>Est. Size</span>
              <span style={styles.backupInfoValue}>
                {people.length === 0
                  ? "~1 KB"
                  : `~${Math.max(1, Math.round(JSON.stringify(buildExport()).length / 1024))} KB`}
              </span>
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={people.length === 0 || exporting}
            style={{
              ...styles.saveBtn,
              marginTop: 16,
              opacity: people.length === 0 ? 0.5 : 1,
            }}
          >
            {exporting ? "Exporting..." : "Download Backup File"}
          </button>
          {people.length === 0 && (
            <p style={styles.backupHint}>
              Add some people first to create a backup.
            </p>
          )}
        </div>
      </div>

      {/* Import Section */}
      <div style={styles.backupCard}>
        <div style={styles.backupCardIcon}>📥</div>
        <div style={styles.backupCardContent}>
          <h3 style={styles.backupCardTitle}>Restore from Backup</h3>
          <p style={styles.backupCardDesc}>
            Upload a previously exported PeopleGraph backup file to restore your
            data. You can choose to replace all current data or merge with
            existing entries.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />

          {!importState || importState.status === "error" ? (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ ...styles.backupUploadBtn, marginTop: 16 }}
              >
                Choose Backup File (.json)
              </button>
              {importState?.status === "error" && (
                <div style={styles.backupError}>
                  <span style={{ marginRight: 8 }}>⚠️</span>
                  {importState.message}
                </div>
              )}
            </>
          ) : importState.status === "preview" ? (
            <div style={styles.backupPreview}>
              <div style={styles.backupPreviewHeader}>
                <span style={{ fontSize: 18 }}>📋</span>
                <span style={styles.backupPreviewTitle}>
                  Backup File Summary
                </span>
              </div>
              <div style={styles.backupInfoGrid}>
                <div style={styles.backupInfoItem}>
                  <span style={styles.backupInfoLabel}>People in file</span>
                  <span style={styles.backupInfoValue}>
                    {importState.data.people.length}
                  </span>
                </div>
                <div style={styles.backupInfoItem}>
                  <span style={styles.backupInfoLabel}>Exported on</span>
                  <span style={styles.backupInfoValue}>
                    {new Date(
                      importState.data._meta.exportedAt,
                    ).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </span>
                </div>
                <div style={styles.backupInfoItem}>
                  <span style={styles.backupInfoLabel}>Exported by</span>
                  <span style={styles.backupInfoValue}>
                    {importState.data._meta.exportedBy}
                  </span>
                </div>
                <div style={styles.backupInfoItem}>
                  <span style={styles.backupInfoLabel}>File version</span>
                  <span style={styles.backupInfoValue}>
                    v{importState.data._meta.version}
                  </span>
                </div>
              </div>
              {/* Category breakdown */}
              <div style={styles.backupCategoryBreakdown}>
                {CATEGORIES.map((c) => {
                  const count = importState.catCounts[c.id] || 0;
                  if (count === 0) return null;
                  return (
                    <span
                      key={c.id}
                      style={{ ...styles.backupCatChip, borderColor: c.color }}
                    >
                      {c.icon} {c.label}: {count}
                    </span>
                  );
                })}
              </div>

              {/* Restore mode */}
              <div style={styles.backupModeSection}>
                <label style={styles.fieldLabel}>Restore Mode</label>
                <div style={styles.backupModeOptions}>
                  <label
                    style={{
                      ...styles.backupModeOption,
                      ...(restoreMode === "replace"
                        ? styles.backupModeActive
                        : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name="restoreMode"
                      value="replace"
                      checked={restoreMode === "replace"}
                      onChange={() => setRestoreMode("replace")}
                      style={{ display: "none" }}
                    />
                    <span style={styles.backupModeRadio}>
                      {restoreMode === "replace" ? "◉" : "○"}
                    </span>
                    <div>
                      <div style={styles.backupModeLabel}>Replace All</div>
                      <div style={styles.backupModeDesc}>
                        Remove all current data and replace with the backup.
                        {people.length > 0
                          ? ` This will delete your ${people.length} existing ${people.length === 1 ? "entry" : "entries"}.`
                          : ""}
                      </div>
                    </div>
                  </label>
                  <label
                    style={{
                      ...styles.backupModeOption,
                      ...(restoreMode === "merge"
                        ? styles.backupModeActive
                        : {}),
                    }}
                  >
                    <input
                      type="radio"
                      name="restoreMode"
                      value="merge"
                      checked={restoreMode === "merge"}
                      onChange={() => setRestoreMode("merge")}
                      style={{ display: "none" }}
                    />
                    <span style={styles.backupModeRadio}>
                      {restoreMode === "merge" ? "◉" : "○"}
                    </span>
                    <div>
                      <div style={styles.backupModeLabel}>Merge</div>
                      <div style={styles.backupModeDesc}>
                        Add new entries from the backup while keeping existing
                        data. Duplicates (same ID) will be skipped.
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                <button
                  onClick={() => setImportState(null)}
                  style={styles.cancelBtn}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (restoreMode === "replace" && people.length > 0) {
                      if (
                        !confirm(
                          `This will replace all ${people.length} existing entries. This cannot be undone. Continue?`,
                        )
                      )
                        return;
                    }
                    handleRestore();
                  }}
                  style={{
                    ...styles.saveBtn,
                    background:
                      restoreMode === "replace" ? "#C2785C" : "#4A7C6F",
                  }}
                >
                  {restoreMode === "replace"
                    ? "Replace & Restore"
                    : "Merge Data"}
                </button>
              </div>
            </div>
          ) : importState.status === "success" ? (
            <div style={styles.backupSuccess}>
              <span style={{ fontSize: 32 }}>✅</span>
              <p style={styles.backupSuccessText}>{importState.message}</p>
              <button
                onClick={() => setImportState(null)}
                style={{ ...styles.cancelBtn, marginTop: 10 }}
              >
                Done
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── PERSON DETAIL MODAL ─────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════
